import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType, useSearchParams } from 'react-router-dom';
import { applyToSearch, canonicalSearch, clearNavScroll, encodeState, getSessionStorage, hasOwnParams, readNav, resolveViewState, shouldRestore, writeNavSearch, type Codec } from './viewState';

/** Pages retain their own state and publish only committed filters, never drafts. */
export function useViewState<T extends object>(codec: Codec<T>, onRestore: (state: T) => void): { initial: T; publish: (state: T) => void } {
  const { pathname, search, key } = useLocation();
  const navigationType = useNavigationType();
  const [, setSearchParams] = useSearchParams();
  const [initial] = useState(() => {
    const storage = getSessionStorage();
    const resolved = resolveViewState(codec, search, readNav(storage, pathname), navigationType);
    if (resolved.clearScroll) clearNavScroll(storage, pathname);
    return resolved.initial;
  });
  const lastSearch = useRef(canonicalSearch(codec, search));
  const restoring = useRef(false);
  restoring.current = false;
  const restoreCallback = useRef(onRestore);
  restoreCallback.current = onRestore;
  const current = useRef({ pathname, search, setSearchParams, codec });
  current.current = { pathname, search, setSearchParams, codec };

  useEffect(() => {
    // Ignore a superseded router snapshot (including StrictMode effect replay).
    if (window.location.pathname !== pathname || canonicalSearch(codec, window.location.search) !== canonicalSearch(codec, search)) return;
    if (!shouldRestore(lastSearch.current, search, codec)) return;
    const storage = getSessionStorage();
    const remembered = readNav(storage, pathname);
    const resolved = resolveViewState(codec, search, remembered, navigationType);
    if (resolved.clearScroll) clearNavScroll(storage, pathname);
    lastSearch.current = canonicalSearch(codec, search);
    restoring.current = true;
    restoreCallback.current(resolved.initial);
    if (navigationType !== 'POP' && !hasOwnParams(codec, new URLSearchParams(search)) && remembered?.search) {
      const next = applyToSearch(codec, new URLSearchParams(search), resolved.initial);
      // Do not redirect a navigation that has already left this page.
      if (window.location.pathname === pathname) {
        try {
          setSearchParams(next, { replace: true });
          lastSearch.current = canonicalSearch(codec, next.toString());
        } catch { /* History rate limits must not prevent restoration. */ }
      }
    }
  }, [key]);

  const publish = useCallback((state: T) => {
    const live = current.current;
    const owned = new URLSearchParams(encodeState(live.codec, state)).toString();
    // A page effect from the restoring render still has the old field values.
    if (restoring.current) return;
    const location = window.location;
    const samePath = location.pathname === live.pathname;
    // Wait for the restoration effect if a same-route navigation is pending.
    if (samePath && shouldRestore(lastSearch.current, location.search, live.codec)
      && canonicalSearch(live.codec, live.search) !== canonicalSearch(live.codec, location.search)) return;
    writeNavSearch(getSessionStorage(), live.pathname, owned ? `?${owned}` : '');
    if (!samePath) return;
    const params = new URLSearchParams(location.search);
    const next = applyToSearch(live.codec, params, state).toString();
    if (next !== params.toString()) {
      try {
        live.setSearchParams(new URLSearchParams(next), { replace: true });
        lastSearch.current = canonicalSearch(live.codec, next);
      } catch { /* Safari may rate-limit replaceState; storage was saved above. */ }
    }
  }, []);
  return { initial, publish };
}
